import React, { useEffect, useMemo, useRef, useState } from 'react'
import { SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import SwiperCore, { FreeMode, Navigation } from 'swiper'
import {
  CustomPosterSwiper,
  CustomSwiper,
  RailTitle,
} from '../MainRailPage/MainRailPage.style'
import { ILightRailItem, railItemToLight } from '../../../types'
import {
  SeparateRailShowWrap,
  SeparateRailWrap,
} from '../ComponentsFactory/ComponentsFactory.style'
import { useOttOverlay } from '../../../contexts/hooks/useOttOverlay'
import { SearchApi } from '../../Api'
import { NextButton, PrevButton } from '../SideButtons/SideButtons'
import { useOttStore } from '../../store/ottStore'
import { RailItem } from '../RailItem/RailItem'
import { getRailConfig } from '../ComponentsFactory/Helpers'
import { RailItemRegularProgram } from '../RailItemRegularProgram/RailItemRegularProgram'

SwiperCore.use([Navigation, FreeMode])

interface Config {
  typeValue: number
}

export const RailSearch = ({
  railType,
  isEmptyData,
}: {
  railType: 'episodes' | 'shows' | 'catchup' | 'movies'
  isEmptyData: (emptyData: boolean) => void
}) => {
  const [searchQuery] = useOttStore(state => [state.searchQuery])
  const lastSearch = useRef('')
  const lastPage = useRef(0)
  const fetching = useRef(false)
  const setFetching = (val: boolean) => (fetching.current = val)
  const [railItems, setRailItems] = useState<ILightRailItem[] | null>(null)
  const [pageIndex, setPageIndex] = useState<number>(1)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [nextButton, setNextButton] = useState<boolean>(true)
  const [prevButton, setPrevButton] = useState<boolean>(false)
  // const [init, setInit] = useState<boolean>(false)
  const swiperRef = useRef<SwiperCore>()
  const { ks } = useOttOverlay()

  function getConfigForType(
    type: 'episodes' | 'shows' | 'catchup' | 'movies'
  ): Config {
    switch (type) {
      case 'episodes':
        return {
          typeValue: 1268,
        }
      case 'shows':
        return {
          typeValue: 1259,
        }
      case 'movies':
        return {
          typeValue: 1262,
        }
      case 'catchup':
        return {
          typeValue: 0,
        }
    }
  }

  const { typeValue } = getConfigForType(railType)
  const railConfig = getRailConfig({ railName: railType })

  useEffect(() => {
    if (railType && railItems && railConfig.size) {
      if (railItems.length > railConfig.size.railSize) {
        setNextButton(true)
      }
      if (railItems.length < railConfig.size.pageSize) {
        setNextButton(false)
        setPrevButton(false)
      }
    }
  }, [railType, railItems, railConfig.size])

  useEffect(() => {
    if (
      !fetching.current &&
      (lastPage.current !== pageIndex || lastSearch.current !== searchQuery) &&
      railConfig.size
    ) {
      setFetching(true)
      SearchApi({
        ks,
        searchQuery,
        typeValue,
        pageIndex,
        pageSize: railConfig.size.railSize,
      }).then(items => {
        setFetching(false)
        if (items?.result?.objects) {
          if (lastSearch.current !== searchQuery) {
            setTotalCount(items.result.totalCount || 0)
            setRailItems(items.result.objects.map(railItemToLight))
            isEmptyData(false)
            lastSearch.current = searchQuery
            lastPage.current = pageIndex
            setNextButton(true)
            setPrevButton(false)
            if (swiperRef.current && railItems) {
              swiperRef.current?.slideTo(0, 0)
            }
            if (items.result.objects.length < railConfig.size.pageSize) {
              setNextButton(false)
            }
          } else if (pageIndex > 1 && railItems) {
            setRailItems([
              ...railItems,
              ...items.result.objects.map(railItemToLight),
            ])
            lastSearch.current = searchQuery
            lastPage.current = pageIndex
          }
        } else {
          setRailItems(null)
          isEmptyData(true)
          lastSearch.current = searchQuery
          lastPage.current = pageIndex
        }
      })
    }
  }, [
    ks,
    pageIndex,
    searchQuery,
    railItems,
    totalCount,
    typeValue,
    isEmptyData,
    railConfig.size,
  ])

  useEffect(() => {
    setPageIndex(1)
  }, [searchQuery])

  const swiperConfig = useMemo(() => {
    if (railType) {
      const railConfig = getRailConfig({
        railName: railType,
      })
      return {
        rtl: 'true',
        slidesPerView: railConfig?.media?.slidesPerView,
        spaceBetween: railConfig?.media?.spaceBetween,
        freeMode: true,
        breakpoints: {
          760: {
            allowTouchMove: false,
            watchSlidesProgress: true,
            slidesPerView: railConfig?.regular?.slidesPerView,
            spaceBetween: railConfig?.regular?.spaceBetween,
            slidesPerGroup: railConfig?.regular?.slidesPerGroup,
            speed: 900,
            centeredSlides: false,
            navigation: {
              nextEl: '.swiper-button-next-custom',
              prevEl: '.swiper-button-prev-custom',
            },
            allowSlidePrev: true,
            allowSlideNext: true,
          },
        },
        // onInit: () => {
        //   setInit(true)
        // },
      }
    }
    return {}
  }, [railType])

  if (!railItems) {
    return null
  }

  const episodesRail = () => {
    return (
      <SeparateRailWrap
        programWidth={90}
        showItemTitle={true}
        theme={{ show: true }}
      >
        {railType === 'episodes' ? (
          <RailTitle>{`פרקים מלאים (${totalCount})`}</RailTitle>
        ) : railType === 'movies' ? (
          <RailTitle>{`כל הקטעים (${totalCount})`}</RailTitle>
        ) : (
          <RailTitle>{`Shows (${totalCount})`}</RailTitle>
        )}

        <CustomSwiper
          {...swiperConfig}
          onInit={el => (swiperRef.current = el)}
          onReachEnd={() => {
            if (totalCount === railItems?.length) {
              setPrevButton(true)
              setNextButton(false)
            } else if (totalCount !== railItems?.length) {
              setPageIndex(prevIndex => prevIndex + 1)
            }
          }}
          onReachBeginning={() => {
            setPrevButton(false)
            setNextButton(true)
          }}
        >
          <PrevButton
            prevButton={prevButton}
            toggleNextButton={nextButton => setNextButton(nextButton)}
          />
          {railItems?.map((r, i) => (
            <SwiperSlide key={r.id}>
              <RailItem
                data={r}
                isFirst={i === 0}
                isLast={i === railItems.length - 1}
                showItemTitle={true}
                timeDuration={railType === 'catchup'}
              />
            </SwiperSlide>
          ))}
          <NextButton
            nextButton={nextButton}
            togglePrevButton={prevButton => setPrevButton(prevButton)}
          />
        </CustomSwiper>
      </SeparateRailWrap>
    )
  }
  const showsRail = () => {
    return (
      <SeparateRailShowWrap programWidth={91} showItemTitle={true}>
        <RailTitle>{` תוכניות (${totalCount})`}</RailTitle>
        <CustomPosterSwiper
          onInit={el => (swiperRef.current = el)}
          {...swiperConfig}
          onReachEnd={() => {
            if (totalCount === railItems?.length) {
              setPrevButton(true)
              setNextButton(false)
            } else if (totalCount !== railItems?.length) {
              setPageIndex(prevIndex => prevIndex + 1)
            }
          }}
          onReachBeginning={() => {
            setPrevButton(false)
            setNextButton(true)
          }}
        >
          <PrevButton
            prevButton={prevButton}
            toggleNextButton={nextButton => setNextButton(nextButton)}
          />
          {railItems?.map((r, i) => (
            <SwiperSlide key={i + '' + r.id}>
              <RailItemRegularProgram
                key={r.id}
                data={r}
                isFirst={i === 0}
                isLast={i === railItems.length - 1}
                railType={'poster_regular'}
                showItemTitle={true}
              />
            </SwiperSlide>
          ))}
          <NextButton
            nextButton={nextButton}
            togglePrevButton={prevButton => setPrevButton(prevButton)}
          />
        </CustomPosterSwiper>
      </SeparateRailShowWrap>
    )
  }

  switch (railType) {
    case 'episodes':
    case 'catchup':
      return episodesRail()
    case 'movies':
      return episodesRail()
    case 'shows':
      return showsRail()
    default:
      return null
  }
}
